// external imports
import omit from "lodash/omit";
import { generateMutation, MutationType, QueryType } from 'typegraph';

// internal imports
import Service from "core/decorators/Service";
import IMutationService, { IMutationResult } from "core/service/mutation/IMutationService";
import IResolverService from "core/service/resolver/IResolverService";
import Class from 'core/util/Class';
import IEntity from 'core/util/IEntity';
import Logger from 'core/util/Logger';
import { actionResultFrom } from 'core/util/MutationUtil';
import ServiceRegistry from "core/util/ServiceRegistry";
import { iDef } from "core/util/TypeUtils";

/**
 * @overview: Implementation of {@link IMutationService}.
 * This should be used any time crud operations need to be performed.
 *
 * @author: jesposito
 */

@Service()
export class MutationService extends IMutationService {

	/**
	 * Automatically calls create of update depening on wheather id field is present.
	 *
	 * @param {T} entity
	 * @param {Class<T extends IEntity>} entityClass
	 */

	public save<T extends IEntity>(entity: T, entityClass?: Class<T>): Promise<IMutationResult<T>> {
		if(entity && entity.id) {
			return this.update(entity, entityClass);
		}

		return this.create(entity, entityClass);
	}

	/**
	 * Creates an entity
	 *
	 * @param entity 	any class that implements {@link IEntity}
	 */
	public create<T extends IEntity>(entity: T, entityClass?: Class<T>): Promise<IMutationResult<T>> {

		// determine the class to use with the query
		const clazz = iDef(entityClass) ? entityClass : Object.getPrototypeOf(entity);

		// generate the query using TypeGraph
		const query = this.getQuery(MutationType.CREATE, entity, clazz);

		// get the http resolver service
		const resolver = ServiceRegistry.get(IResolverService);

		// return the promise
		return IMutationService.parseResponse(resolver.execute(query)).then((result) => {
			resolver.clearCache();
			return result;
		}) as any;
	}

	/**
	 * Updates an entity
	 *
	 * @param entity 	any class that implements {@link IEntity}
	 */
	public update<T extends IEntity>(entity: T, entityClass?: Class<T>): Promise<IMutationResult<T>> {

		// determine the class to use with the query
		const clazz = iDef(entityClass) ? entityClass : Object.getPrototypeOf(entity);

		// generate the query using TypeGraph
		const query = this.getQuery(MutationType.UPDATE, entity, clazz);

		// get the http resolver service
		const resolver = ServiceRegistry.get(IResolverService);

		// return the promise
		return IMutationService.parseResponse(resolver.execute(query)).then((result) => {
			resolver.clearCache();
			return result;
		}) as any;
	}

	/**
	 * Deletes an entity
	 *
	 * @param data			any instnace of a class that implements {@link IEntity}
	 * @param entityClass	OPTIONAL unconstructed class that implements IEntity
	 */
	public delete<T extends IEntity>(entity: T, entityClass?: Class<T>): Promise<IMutationResult<T>> {

		// determine the class to use with the query
		const clazz = iDef(entityClass) ? entityClass : Object.getPrototypeOf(entity);

		// generate the query using TypeGraph. need to convert to ActionResult for delete methods.
		const query = this.getQuery(MutationType.DELETE, entity, actionResultFrom(clazz));

		// get the http resolver service
		const resolver = ServiceRegistry.get(IResolverService);

		// return the promise
		return IMutationService.parseResponse(resolver.execute(query)).then((result) => {
			resolver.clearCache();
			return result;
		}) as any;
	}

	/**
	 * Centralized logic for generating mutation query. We assume that
	 * the paramter format is the same for all mutation schemas. This is
	 * also enforced in the graphql controller.
	 *
	 * @param type 		the mutation type
	 * @param entity 	any class that implements {@link IEntity}
	 */
	private getQuery<T extends IEntity>(type: MutationType, data: T, entityClass: Class<T>) {

		// remove apollo added fields as they will cause gql validation errors
		const sanitized = omit(data, "__typename");

		// create the params. our entity instance is payload here
		// this assumes that the paramter schema for mutations is the same
		const params = { params: sanitized };

		// for mutations the query is not generated from the fields of the entity,
		// instead we specify them. for now the assumption is that all mutations
		// will resolve these fields. this is equivalent to IMutationResult.
		/*
		const fields = {
			entityId: {},
			operationResult: {},
			operationPerformed: {}
		};
		*/

		// we want the Class (prototype / function) not the instance (object)
		// so the @Entity decorator can be read to get the mutation names
		// const clazz: Class<any> = actionResultFrom(entityClass);

		// generate the mutation using TypeGraph
		const mutation = generateMutation(entityClass, type, params, "entity");

		// output in development mode only
		Logger.debug("generated mutation: %s", mutation);

		return mutation;
	}

	public getServiceId(): string {
		return IMutationService.DEFAULT_ID;
	}
}



// WEBPACK FOOTER //
// ./src/core/service/mutation/impl/Mutation.service.ts